/**
 * The charts on the Analytics page, drawn as plain SVG.
 *
 * No charting library, on purpose (CLAUDE.md, "don't add dependencies
 * casually"): these are seven small shapes, and a library would be the
 * largest thing in the bundle to draw them.
 *
 * Every chart scales to its container through `viewBox`, takes its numbers
 * already worked out by the API, and carries an `aria-label` that says what it
 * shows, because the figures it draws are also on the page as text.
 *
 * ## Every chart names its axes and answers a hover
 *
 * Emmanuel's review, 13 October 2026: "every chart should have an x axis label
 * and when the cursor hovers on it it should show some info". Each chart is
 * wrapped in a `Frame` that carries the axis titles and a box that follows the
 * pointer; each mark says what it stands for through `on(...)`.
 *
 * The chart colours were chosen by Emmanuel from a reference dashboard on
 * 11 October 2026 (docs/DECISIONS.md). They are for data only: buttons, tags
 * and text keep the app's own palette.
 */
import { useRef, useState, type ReactNode } from 'react';
import { c, chart, mono, sans } from '../theme.js';

const axisText = { fontFamily: mono, fontSize: 10.5, fill: c.faint } as const;

// ------------------------------------------------------------- hover and axes

interface Tip {
  x: number;
  y: number;
  title: string;
  lines: string[];
}

/**
 * The box that follows the pointer over a chart.
 *
 * A mark calls `on(title, lines)` and spreads the result onto itself. Keyboard
 * and touch users are not left out by this: every figure a tip shows is also
 * on the page as text or in the chart's `aria-label`.
 */
function useTip() {
  const frame = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);

  function show(e: React.MouseEvent, title: string, lines: string[]) {
    const box = frame.current?.getBoundingClientRect();
    if (!box) return;
    setTip({ x: e.clientX - box.left, y: e.clientY - box.top, title, lines });
  }

  const on = (title: string, lines: string[]) => ({
    onMouseEnter: (e: React.MouseEvent) => show(e, title, lines),
    onMouseMove: (e: React.MouseEvent) => show(e, title, lines),
    onMouseLeave: () => setTip(null),
  });

  return { frame, tip, on };
}

function Frame({
  frame,
  tip,
  xLabel,
  yLabel,
  children,
}: {
  frame: React.RefObject<HTMLDivElement | null>;
  tip: Tip | null;
  /** What runs along the bottom, e.g. "Month". */
  xLabel?: string | undefined;
  /** What the height or length of a mark measures, e.g. "Cost of stock (£)". */
  yLabel?: string | undefined;
  children: ReactNode;
}) {
  const width = frame.current?.clientWidth ?? 0;
  // Keep the box inside the frame: put it to the left of the pointer when it
  // would run past the right edge.
  const flip = tip !== null && width > 0 && tip.x > width - 200;
  return (
    <div ref={frame} style={{ position: 'relative' }}>
      {yLabel && <div style={{ fontSize: 11.5, color: c.faint, marginBottom: 4 }}>{yLabel}</div>}
      {children}
      {xLabel && (
        <div style={{ fontSize: 11.5, color: c.faint, textAlign: 'center', marginTop: 3 }}>
          {xLabel}
        </div>
      )}
      {tip && (
        <div
          role="status"
          style={{
            position: 'absolute',
            ...(flip ? { right: width - tip.x + 14 } : { left: tip.x + 14 }),
            top: Math.max(0, tip.y - 12),
            background: c.ink,
            color: c.white,
            borderRadius: 10,
            padding: '8px 11px',
            fontSize: 12.5,
            lineHeight: 1.45,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            zIndex: 5,
            boxShadow: '0 8px 22px -8px rgba(20,26,22,0.45)',
          }}
        >
          <div style={{ fontWeight: 500 }}>{tip.title}</div>
          {tip.lines.map((line) => (
            <div key={line} style={{ color: '#C9CEC7' }}>
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The first and last names under a small chart: its x axis, in two words. */
function Ends({
  first,
  last,
  color,
}: {
  first: string | undefined;
  last: string | undefined;
  color?: string | undefined;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontFamily: mono,
        fontSize: 10.5,
        color: color ?? c.faint,
        marginTop: 3,
        padding: '0 4px',
      }}
    >
      {/* "May 2026" on hover, "May" here: the tile is narrow. */}
      <span>{first?.split(' ')[0]}</span>
      <span>{last?.split(' ')[0]}</span>
    </div>
  );
}

/** A round number at or above `max`, so the top gridline is a clean figure. */
export function niceCeil(max: number): number {
  if (max <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (step * pow >= max) return step * pow;
  }
  return 10 * pow;
}

function gridlines(
  max: number,
  left: number,
  right: number,
  top: number,
  height: number,
  format: (n: number) => string,
) {
  return [0, 0.25, 0.5, 0.75, 1].map((t) => {
    const y = top + height * (1 - t);
    return (
      <g key={t}>
        <line
          x1={left}
          x2={right}
          y1={y}
          y2={y}
          stroke={c.border}
          strokeDasharray={t === 0 ? undefined : '3 4'}
        />
        <text x={left - 8} y={y + 3.5} textAnchor="end" style={axisText}>
          {format(max * t)}
        </text>
      </g>
    );
  });
}

// --------------------------------------------------------------------- charts

/** A small trend line with a soft fill and a dot on the latest point. */
export function Spark({
  values,
  names,
  format,
  color,
  fill,
  label,
  axisColor,
}: {
  values: number[];
  /** One name per value, e.g. the month. Shown under the chart and on hover. */
  names: string[];
  format: (n: number) => string;
  color: string;
  fill: string;
  label: string;
  axisColor?: string;
}) {
  const { frame, tip, on } = useTip();
  const w = 240;
  const h = 46;
  if (values.length < 2) return <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} />;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const band = (w - 8) / (values.length - 1);
  const pts = values.map((v, i) => [4 + i * band, 4 + (h - 8) * (1 - (v - min) / range)] as const);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1]!;
  return (
    <Frame frame={frame} tip={tip}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} style={{ display: 'block' }}>
        <path d={`${d} L${last[0]} ${h} L4 ${h} Z`} fill={fill} />
        <path d={d} fill="none" stroke={color} strokeWidth={2} />
        <circle cx={last[0]} cy={last[1]} r={3.5} fill={color} />
        {/* A wide invisible strip over each point, so there is something to hover. */}
        {pts.map((p, i) => (
          <rect
            key={i}
            x={p[0] - band / 2}
            y={0}
            width={band}
            height={h}
            fill="transparent"
            {...on(names[i] ?? '', [format(values[i] ?? 0)])}
          />
        ))}
      </svg>
      <Ends first={names[0]} last={names[names.length - 1]} color={axisColor} />
    </Frame>
  );
}

/** A row of small bars; the latest is drawn in the stronger colour. */
export function MiniBars({
  values,
  names,
  format,
  color,
  highlight,
  label,
  details,
}: {
  values: number[];
  names: string[];
  format: (n: number) => string;
  color: string;
  highlight: string;
  label: string;
  /** Extra lines for each bar's hover box: what the figure is made of. */
  details?: string[][];
}) {
  const { frame, tip, on } = useTip();
  const w = 240;
  const h = 46;
  const max = Math.max(...values, 1);
  const bw = (w - 8) / Math.max(values.length, 1);
  return (
    <Frame frame={frame} tip={tip}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} style={{ display: 'block' }}>
        {values.map((v, i) => {
          const bh = Math.max(3, ((h - 4) * v) / max);
          return (
            <g key={i} {...on(names[i] ?? '', [format(v), ...(details?.[i] ?? [])])}>
              <rect x={4 + i * bw} y={0} width={bw} height={h} fill="transparent" />
              <rect
                x={4 + i * bw + 2}
                y={h - bh}
                width={Math.max(2, bw - 4)}
                height={bh}
                rx={3}
                fill={i === values.length - 1 ? highlight : color}
              />
            </g>
          );
        })}
      </svg>
      <Ends first={names[0]} last={names[names.length - 1]} />
    </Frame>
  );
}

export interface Series {
  name: string;
  color: string;
  values: number[];
}

/** Several bars per month, side by side. */
export function GroupedBars({
  labels,
  names,
  series,
  format,
  label,
  xLabel,
  yLabel,
  details,
}: {
  labels: string[];
  /** The full name of each group, for hover: "October 2026". */
  names: string[];
  series: Series[];
  format: (n: number) => string;
  label: string;
  xLabel?: string;
  yLabel?: string;
  /** Extra lines for each group's hover box. */
  details?: string[][];
}) {
  const { frame, tip, on } = useTip();
  const w = 760;
  const h = 240;
  const left = 52;
  const bottom = 26;
  const top = 8;
  const ph = h - bottom - top;
  const max = niceCeil(Math.max(...series.flatMap((s) => s.values), 0));
  const gw = (w - left) / Math.max(labels.length, 1);
  const bw = (gw * 0.72) / Math.max(series.length, 1);
  return (
    <Frame frame={frame} tip={tip} xLabel={xLabel} yLabel={yLabel}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} style={{ display: 'block' }}>
        {gridlines(max, left, w, top, ph, format)}
        {labels.map((lab, i) => (
          <g
            key={lab + i}
            {...on(
              names[i] ?? lab,
              [
                ...series.map((s) => `${s.name}: ${format(s.values[i] ?? 0)}`),
                ...(details?.[i] ?? []),
              ],
            )}
          >
            {/* The whole column is the hover target, not just the bars. */}
            <rect x={left + i * gw} y={top} width={gw} height={ph} fill="transparent" />
            {series.map((s, j) => {
              const v = s.values[i] ?? 0;
              const bh = v === 0 ? 0 : Math.max(2, (ph * v) / max);
              return (
                <rect
                  key={s.name}
                  x={left + i * gw + gw * 0.14 + j * bw + 1.5}
                  y={top + ph - bh}
                  width={Math.max(2, bw - 3)}
                  height={bh}
                  rx={3}
                  fill={s.color}
                />
              );
            })}
            <text x={left + i * gw + gw / 2} y={h - 8} textAnchor="middle" style={axisText}>
              {lab}
            </text>
          </g>
        ))}
      </svg>
    </Frame>
  );
}

/** One bar per month with its value written above it. */
export function ColumnBars({
  bars,
  format,
  label,
  xLabel,
  yLabel,
}: {
  bars: { label: string; value: number; color: string; name?: string; lines?: string[] }[];
  format: (n: number) => string;
  label: string;
  xLabel?: string;
  yLabel?: string;
}) {
  const { frame, tip, on } = useTip();
  const w = 640;
  const h = 210;
  const left = 52;
  const bottom = 26;
  const top = 20;
  const ph = h - bottom - top;
  const max = niceCeil(Math.max(...bars.map((b) => b.value), 0));
  const gw = (w - left) / Math.max(bars.length, 1);
  return (
    <Frame frame={frame} tip={tip} xLabel={xLabel} yLabel={yLabel}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} style={{ display: 'block' }}>
        {gridlines(max, left, w, top, ph, format)}
        {bars.map((b, i) => {
          const bh = b.value === 0 ? 0 : Math.max(3, (ph * b.value) / max);
          const x = left + i * gw + gw * 0.24;
          const bw = gw * 0.52;
          return (
            <g
              key={b.label + i}
              {...on(b.name ?? b.label, [
                b.value === 0 ? 'Nothing' : format(b.value),
                ...(b.lines ?? []),
              ])}
            >
              <rect x={left + i * gw} y={top} width={gw} height={ph} fill="transparent" />
              <rect x={x} y={top + ph - bh} width={bw} height={bh} rx={4} fill={b.color} />
              <text
                x={x + bw / 2}
                y={top + ph - bh - 6}
                textAnchor="middle"
                style={{ ...axisText, fill: b.value === 0 ? c.faint : c.ink }}
              >
                {b.value === 0 ? 'none' : format(b.value)}
              </text>
              <text x={x + bw / 2} y={h - 8} textAnchor="middle" style={axisText}>
                {b.label}
              </text>
            </g>
          );
        })}
      </svg>
    </Frame>
  );
}

const RANK_COLORS = [chart.orange, chart.green, chart.navy, chart.amber, chart.green, chart.navy];

/** Ranked horizontal bars, with the name inside the bar when it fits. */
export function RankBars({
  rows,
  label,
  unit,
  xLabel,
}: {
  rows: { name: string; value: number }[];
  label: string;
  /** What a value counts, for the hover text: "units". */
  unit: string;
  xLabel?: string;
}) {
  const { frame, tip, on } = useTip();
  const w = 440;
  const rh = 30;
  const h = rows.length * rh + 4;
  const left = 18;
  const max = Math.max(...rows.map((r) => r.value), 1);
  const track = w - left - 34;
  return (
    <Frame frame={frame} tip={tip} xLabel={xLabel}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} style={{ display: 'block' }}>
        {rows.map((r, i) => {
          const y = i * rh + 3;
          const bw = Math.max(6, (track * r.value) / max);
          // Roughly 6.2px a character at this size; the name goes inside the
          // bar only when it clearly fits, and beside it otherwise.
          const inside = bw > r.name.length * 6.2 + 24;
          return (
            <g key={r.name + i} {...on(r.name, [`${r.value} ${unit}`])}>
              <rect x={0} y={y - 3} width={w} height={rh} fill="transparent" />
              <text x={0} y={y + 16} style={axisText}>
                {i + 1}
              </text>
              <rect x={left} y={y} width={bw} height={rh - 7} rx={5} fill={RANK_COLORS[i % 6]} />
              {inside && (
                <text
                  x={left + 10}
                  y={y + 16}
                  style={{ fontFamily: sans, fontSize: 12, fill: c.white }}
                >
                  {r.name}
                </text>
              )}
              <text x={left + bw + 8} y={y + 16} style={{ ...axisText, fill: c.ink }}>
                {r.value}
              </text>
              {!inside && (
                <text
                  x={left + bw + 14 + String(r.value).length * 7}
                  y={y + 16}
                  style={{ fontFamily: sans, fontSize: 12, fill: c.muted }}
                >
                  {r.name}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </Frame>
  );
}

const WEEKDAYS = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];
const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

/** Seven bars, Monday first, the busiest in the stronger colour. */
export function WeekdayBars({
  values,
  busiest,
  label,
  unit,
  xLabel,
}: {
  values: number[];
  busiest: number | null;
  label: string;
  unit: string;
  xLabel?: string;
}) {
  const { frame, tip, on } = useTip();
  const w = 260;
  const h = 150;
  const bottom = 20;
  const ph = h - bottom - 4;
  const max = Math.max(...values, 1);
  const gw = w / 7;
  return (
    <Frame frame={frame} tip={tip} xLabel={xLabel}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} style={{ display: 'block' }}>
        {WEEKDAYS.map((day, i) => {
          const v = values[i] ?? 0;
          const bh = Math.max(2, (ph * v) / max);
          return (
            <g key={day} {...on(WEEKDAY_NAMES[i] ?? day, [`${v} ${unit}`])}>
              <rect x={i * gw} y={0} width={gw} height={h - bottom} fill="transparent" />
              <rect
                x={i * gw + 5}
                y={4 + ph - bh}
                width={gw - 10}
                height={bh}
                rx={5}
                fill={i === busiest ? chart.green : chart.greenPale}
              />
              <text x={i * gw + gw / 2} y={h - 5} textAnchor="middle" style={axisText}>
                {day}
              </text>
            </g>
          );
        })}
      </svg>
    </Frame>
  );
}

/** A line over a soft fill, with a labelled value axis and the first and last dates. */
export function AreaLine({
  values,
  names,
  startLabel,
  endLabel,
  format,
  label,
  xLabel,
  yLabel,
}: {
  values: number[];
  /** One name per point, for hover: "Week of 14 Jul". */
  names: string[];
  startLabel: string;
  endLabel: string;
  format: (n: number) => string;
  label: string;
  xLabel?: string;
  yLabel?: string;
}) {
  const { frame, tip, on } = useTip();
  const w = 640;
  const h = 130;
  const left = 52;
  const bottom = 22;
  const top = 8;
  const ph = h - bottom - top;
  const max = niceCeil(Math.max(...values, 0));
  const span = Math.max(values.length - 1, 1);
  const band = (w - left - 16) / span;
  const pts = values.map((v, i) => [left + 8 + i * band, top + ph * (1 - v / max)] as const);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const first = pts[0];
  const last = pts[pts.length - 1];
  return (
    <Frame frame={frame} tip={tip} xLabel={xLabel} yLabel={yLabel}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} style={{ display: 'block' }}>
        {[0, 0.5, 1].map((t) => {
          const y = top + ph * (1 - t);
          return (
            <g key={t}>
              <line
                x1={left}
                x2={w}
                y1={y}
                y2={y}
                stroke={c.border}
                strokeDasharray={t === 0 ? undefined : '3 4'}
              />
              <text x={left - 8} y={y + 3.5} textAnchor="end" style={axisText}>
                {format(max * t)}
              </text>
            </g>
          );
        })}
        {first && last && (
          <>
            <path
              d={`${d} L${last[0]} ${top + ph} L${first[0]} ${top + ph} Z`}
              fill="rgba(46,125,107,0.12)"
            />
            <path d={d} fill="none" stroke={chart.green} strokeWidth={2} />
            <text x={first[0]} y={h - 5} style={axisText}>
              {startLabel}
            </text>
            <text x={last[0]} y={h - 5} textAnchor="end" style={axisText}>
              {endLabel}
            </text>
            {pts.map((p, i) => (
              <g key={i} {...on(names[i] ?? '', [format(values[i] ?? 0)])}>
                <rect x={p[0] - band / 2} y={top} width={band} height={ph} fill="transparent" />
                <circle cx={p[0]} cy={p[1]} r={i === pts.length - 1 ? 4 : 2.5} fill={chart.green} />
              </g>
            ))}
          </>
        )}
      </svg>
    </Frame>
  );
}

/** One bar split into shares, with the percentage inside each wide enough part. */
export function SplitBar({
  parts,
}: {
  parts: { label: string; percent: number; color: string; detail?: string }[];
}) {
  const { frame, tip, on } = useTip();
  return (
    <Frame frame={frame} tip={tip}>
      <div
        role="img"
        aria-label={parts.map((p) => `${p.label} ${p.percent}%`).join(', ')}
        style={{ display: 'flex', height: 34, borderRadius: 12, overflow: 'hidden', marginTop: 14 }}
      >
        {parts.map((p) => (
          <div
            key={p.label}
            {...on(p.label, [`${p.percent}% of stock value`, ...(p.detail ? [p.detail] : [])])}
            style={{
              width: `${p.percent}%`,
              background: p.color,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: p.percent >= 10 ? 9 : 0,
              fontSize: 12.5,
              fontFamily: mono,
              color: c.white,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {p.percent >= 10 ? `${p.percent}%` : ''}
          </div>
        ))}
      </div>
    </Frame>
  );
}
